<script lang="ts">
  import { fadeSlide } from "../utils/transition";
  import { circInOut } from "svelte/easing";
  import Form from "../forms/Form.svelte";
  import Input from "../forms/Input.svelte";
  import Button from "../forms/Button.svelte";
  import InputSwitch from "../forms/InputSwitch.svelte";
  import InputRooms from "./controls/InputRooms.svelte";
  import type { FormControl } from "../forms/controls";
  import { createEventDispatcher } from "svelte";
  import InputDateRange from "../forms/InputDateRange.svelte";
  import FormActions from "./controls/FormActions.svelte";
  import type { WidgetLayout } from "./services/config";
  import type { TravelDataFormValue } from "./services/formModels";
  import CheckboxGroup from "./controls/CheckboxGroup.svelte";
  import GroupOption from "./controls/GroupOption.svelte";
  import type { Localizer } from "../l10n/localization.service";
  import type { EnquiryService } from "./services/enquiryService";
  import FormRequired from "./controls/FormRequired.svelte";

  export let form: FormControl<TravelDataFormValue>;
  export let layout: WidgetLayout;
  export let T: Localizer;
  export let enquiryService: EnquiryService;

  const filterOptions = enquiryService.options.filter || {};

  let dispatch = createEventDispatcher();

  enquiryService.options.trackStep("travel-data");

  function handleSubmit(e) {
    if (form.validate(true)) {
      dispatch("submit", form);
    }
  }
</script>

<Form bind:form on:submit={handleSubmit}>
  <div class="form-layout layout-{layout}" class:direct-enquiry={enquiryService.options.enquiryType == 2}>
    <div class="range">
      <InputDateRange name="range" displayName={T.get("travelData.range")} language={enquiryService.options.language} required />
    </div>

    <div class="rooms">
      <InputRooms name="rooms" {T} />
    </div>

    {#if enquiryService.options.enquiryType == 2}
      <div class="boardings">
        <CheckboxGroup name="boardingIds" title={T.get("travelData.boards")}>
          {#each filterOptions.boards as board (board)}
            <GroupOption value={board.id} label={board.name} />
          {/each}
        </CheckboxGroup>
      </div>
    {/if}

    <div class="pets">
      <InputSwitch name="hasPets">{T.get("travelData.hasPets")}</InputSwitch>
    </div>

    {#if form.get("hasPets")?.value}
      <div class="pets-message" transition:fadeSlide|local={{ duration: 150, easing: circInOut }}>
        <div>
          <Input type="text" name="petsMessage" rows={4} placeholder={T.get("travelData.petsMessagePlaceholder")} displayName={T.get("travelData.petsMessage")} required />
        </div>
      </div>
    {/if}
  </div>

  <FormRequired {T} />

  <FormActions sticky={layout == "default"}>
    <Button type="submit">{T.get("travelData.submit")}</Button>
  </FormActions>
</Form>

<style lang="scss">
  @import "./_mixins.scss";

  .form-layout {
    display: grid;
    grid-template:
      "range range" auto
      "rooms pets" auto
      "pets-message pets-message" auto / 1fr 1fr;
    gap: 1.2rem;

    &.direct-enquiry {
      grid-template:
        "range range" auto
        "rooms boardings" auto
        "pets boardings" auto
        "pets-message pets-message" auto / 1fr 1fr;
    }

    @include media-breakpoint-mobile {
      grid-template:
        "range" auto
        "rooms" auto
        "pets" auto
        "pets-message" auto / 100%;

      &.direct-enquiry {
        grid-template:
          "range" auto
          "rooms" auto
          "boardings" auto
          "pets" auto
          "pets-message" auto / 100%;
      }
    }
  }

  .range {
    grid-area: range;
  }

  .boardings {
    grid-area: boardings;
  }
  .rooms {
    grid-area: rooms;
  }

  .pets {
    grid-area: pets;
    display: flex;
    align-items: center;
  }

  .pets-message {
    grid-area: pets-message;
  }

  // layouts:
  .layout-default {
    background-color: #f7f7f7;
    border: solid 2px #d9d9d9;
    border-radius: 28px;
    padding: 1.2em;
    padding-bottom: 0;

    .pets-message > div {
      padding-bottom: 1.2rem;
    }
  }
</style>
